'use client';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Link from 'next/link';

interface MarkdownRendererProps {
  content: string;
}

export default function MarkdownRenderer({ content }: MarkdownRendererProps) {
  if (!content) return null;

  return (
    <div className="prose prose-invert prose-lg max-w-none
      prose-headings:text-white 
      prose-p:text-slate-300 prose-p:leading-relaxed
      prose-a:text-amber-500 prose-a:no-underline hover:prose-a:underline
      prose-strong:text-white 
      prose-code:text-amber-400 prose-code:bg-slate-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:text-sm
      prose-pre:bg-slate-900 prose-pre:border prose-pre:border-slate-800 prose-pre:rounded-lg
      prose-blockquote:border-l-amber-500 prose-blockquote:text-slate-400 prose-blockquote:italic
      prose-li:text-slate-300
      prose-img:rounded-lg
      prose-table:border-slate-700
      prose-th:text-white prose-th:border-slate-700
      prose-td:border-slate-700"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-4xl font-bold mt-12 mb-6 text-white">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-3xl font-bold mt-10 mb-4 text-white">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-2xl font-semibold mt-8 mb-3 text-white">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-xl font-semibold mt-6 mb-2 text-white">{children}</h4>
          ),
          p: ({ children }) => (
            <p className="text-slate-300 leading-relaxed mb-4">{children}</p>
          ),
          a: ({ href, children }) => (
            <Link
              href={href || '#'}
              className="text-amber-500 hover:text-amber-400 underline underline-offset-2 transition-colors"
            >
              {children}
            </Link>
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-6 pl-6 border-l-4 border-amber-500 italic text-slate-400">
              {children}
            </blockquote>
          ),
          code: ({ className, children }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <code className="bg-slate-800 text-amber-400 px-1.5 py-0.5 rounded text-sm font-mono">
                  {children}
                </code>
              );
            }
            return (
              <code className={`${className} text-slate-300`}>{children}</code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-slate-900 border border-slate-800 p-4 rounded-lg overflow-x-auto text-sm">
              {children}
            </pre>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-2 text-slate-300 my-4 ml-4">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside space-y-2 text-slate-300 my-4 ml-4">
              {children}
            </ol>
          ),
          li: ({ children }) => <li>{children}</li>,
          table: ({ children }) => (
            <div className="overflow-x-auto my-6">
              <table className="min-w-full divide-y divide-slate-700 border border-slate-700 rounded-lg">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-slate-800">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3 text-left text-sm font-semibold text-white border-b border-slate-700">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3 text-sm text-slate-300 border-b border-slate-800">
              {children}
            </td>
          ),
          hr: () => (
            <hr className="my-12 border-0 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

